/**
 * The billing-address country dropdown.
 *
 * `User.billingCountry` is `VarChar(2)` and `AccountService.normalizeCountry`
 * accepts exactly two letters, so the VALUE is always an ISO 3166-1 alpha-2
 * code. Only the codes are stored here; the human names come from
 * `Intl.DisplayNames`, which every browser this game runs in has had for
 * years, so 250 English names do not have to ship in the bundle and a player
 * on a German locale sees German country names for free.
 *
 * A hand-written `{ code, name }` table was the obvious alternative and is
 * worse in the way that matters: it is 250 rows nobody will ever proofread,
 * in one language, that go stale silently.
 *
 * Fallback is the CODE, not a blank row — a dropdown entry reading "PL" is
 * usable; one reading "" is not.
 */

/**
 * Every ISO 3166-1 alpha-2 code currently assigned, as one string.
 *
 * Kept as a string rather than an array literal because it is data, not code,
 * and 250 quoted entries would be 250 lines of diff the next time the list
 * changes. Split once, at module load.
 */
const ALPHA2 = 'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN '
  + 'BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM '
  + 'DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU '
  + 'GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY '
  + 'KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW '
  + 'MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE '
  + 'RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK '
  + 'TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW';

export interface Country {
  code: string;
  name: string;
}

let cached: Country[] | null = null;

/**
 * The dropdown's options, sorted by the NAME the player will read.
 *
 * Sorted with `localeCompare` rather than by code: an alphabetical list of
 * codes puts Germany under D and the Netherlands under N, which is only
 * alphabetical to someone reading the codes.
 *
 * Built once and cached — `Intl.DisplayNames` is not free, and this runs on
 * every render of the billing form.
 */
export function countries(): Country[] {
  if (cached) return cached;
  const codes = ALPHA2.split(' ');
  let name: (code: string) => string = (code) => code;
  try {
    const display = new Intl.DisplayNames(undefined, { type: 'region' });
    name = (code) => display.of(code) ?? code;
  } catch {
    // No Intl.DisplayNames, or no region data for this locale. Codes alone are
    // still a working dropdown, which is the point of the fallback.
  }
  cached = codes
    .map((code) => ({ code, name: name(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cached;
}
